import React from 'react';
import { useListForm } from '../../hooks/useListForm';
import { useSceneUpdate } from '../../hooks/useSceneUpdate';
import './OpenWorldSceneCharacters.css';
import CustomSelect from '../CustomSelect';

const INITIAL_DATA = {
  character_id: '',
  sprite_mix_id: '',
  start_sprite_id: '',
  starts: false
};

const OpenWorldSceneCharacters = ({
  novelId, sceneId, scene, setScene, novel, loading, setLoading, error, setError, isProduction = false
}) => {
  const { showForm, editingIndex, formData, setFormData, openAdd, openEdit, close } = useListForm(INITIAL_DATA);
  const { saveScene } = useSceneUpdate(novelId, sceneId, isProduction, setScene, setLoading, 'open_world');

  const handleEditClick = (character, index) => {
    openEdit({
      character_id: character.character_id,
      sprite_mix_id: character.sprite_mix_id,
      start_sprite_id: character.start_sprite_id,
      starts: character.starts || false
    }, index);
  };

  const handleSave = async () => {
    if (!formData.character_id) {
      setError('Выберите персонажа');
      return;
    }
    setError(null);
    const updated = [...(scene?.character_custom || [])];
    const item = {
      character_id: formData.character_id,
      sprite_mix_id: formData.sprite_mix_id || null,
      start_sprite_id: formData.start_sprite_id || null,
      starts: formData.starts
    };
    if (editingIndex !== null) {
      updated[editingIndex] = item;
    } else {
      updated.push(item);
    }
    const success = await saveScene({ ...scene, character_custom: updated }, 'Персонаж успешно сохранен!');
    if (success) close();
  };

  const handleDelete = async (index) => {
    if (!window.confirm('Вы уверены, что хотите удалить этого персонажа из сцены?')) return;
    const updated = [...(scene?.character_custom || [])];
    updated.splice(index, 1);
    await saveScene({ ...scene, character_custom: updated }, 'Персонаж успешно удален!');
  };

  const getSelectedCharacter = () => novel?.characters?.find(char => char.id === formData.character_id) || null;
  const getSelectedSpriteMix = () => getSelectedCharacter()?.sprites?.find(sprite => sprite.id === formData.sprite_mix_id) || null;
  const getCharacterName = (id) => novel?.characters?.find(char => char.id === id)?.prompt_name || 'Неизвестный персонаж';
  const getSpriteMixName = (charId, mixId) => novel?.characters?.find(c => c.id === charId)?.sprites?.find(s => s.id === mixId)?.name || 'Неизвестный микс';
  const getSpriteAssetName = (spriteId) => novel?.assets?.sprites?.find(a => a.id === spriteId)?.name || 'Неизвестный спрайт';

  return (
    <div className="owcha-wrap">
      <div className="owcha-header">
        <h3 className="owcha-title">Персонажи сцены (Открытый мир)</h3>
      </div>

      <div className="owcha-add-wrap">
        <button className="owcha-add-btn" onClick={openAdd} disabled={loading}>
          <span className="button-icon">➕</span>
          Добавить персонажа
        </button>
      </div>

      {error && <div className="owcha-error">{error}</div>}

      {showForm && (
        <div className="owcha-overlay">
          <div className="owcha-modal">
            <h3 className="owcha-modal-title">{editingIndex !== null ? 'Редактировать персонажа' : 'Добавить персонажа'}</h3>

            <div className="owcha-form-group">
              <label className="owcha-label">Персонаж:</label>
              <CustomSelect
                value={formData.character_id}
                onChange={(e) => setFormData({ ...formData, character_id: e.target.value, sprite_mix_id: '', start_sprite_id: '' })}
                className="app-select" disabled={loading} required
              >
                <option value="">Выберите персонажа</option>
                {novel?.characters?.map((character) => (
                  <option key={character.id} value={character.id}>{character.prompt_name}</option>
                ))}
              </CustomSelect>
            </div>

            <div className="owcha-form-group">
              <label className="owcha-label">Набор спрайтов:</label>
              <CustomSelect
                value={formData.sprite_mix_id}
                onChange={(e) => setFormData({ ...formData, sprite_mix_id: e.target.value, start_sprite_id: '' })}
                className="app-select" disabled={loading || !formData.character_id}
              >
                <option value="">Нет (Без спрайтов)</option>
                {getSelectedCharacter()?.sprites?.map((spriteMix) => (
                  <option key={spriteMix.id} value={spriteMix.id}>{spriteMix.name}</option>
                ))}
              </CustomSelect>
            </div>

            <div className="owcha-form-group">
              <label className="owcha-label">Начальный спрайт:</label>
              <CustomSelect
                value={formData.start_sprite_id}
                onChange={(e) => setFormData({ ...formData, start_sprite_id: e.target.value })}
                className="app-select" disabled={loading || !formData.sprite_mix_id}
              >
                <option value="">Выберите начальный спрайт</option>
                {getSelectedSpriteMix()?.sprite_ids?.map((spriteId) => (
                  <option key={spriteId} value={spriteId}>{getSpriteAssetName(spriteId)}</option>
                ))}
              </CustomSelect>
            </div>

            <div className="owcha-form-group">
              <div className="owcha-checkbox-wrap">
                <input
                  type="checkbox" className="owcha-checkbox"
                  checked={formData.starts}
                  onChange={(e) => setFormData({ ...formData, starts: e.target.checked })}
                  disabled={loading}
                />
                <label className="owcha-starts-label">Начинает в сцене</label>
              </div>
            </div>

            <div className="owcha-form-actions">
              <button type="button" onClick={handleSave} disabled={loading} className="owcha-btn-primary">
                {loading ? 'Загрузка...' : 'Сохранить'}
              </button>
              <button type="button" onClick={() => { close(); setError(null); }} disabled={loading} className="owcha-btn-cancel">
                Отменить
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="owcha-list">
        {scene?.character_custom?.length > 0 ? (
          scene.character_custom.map((character, index) => (
            <div key={index} className="owcha-card">
              <h4 className="owcha-card-name">
                {getCharacterName(character.character_id)}
                {character.starts && <span className="owcha-starts-badge">Стартовый</span>}
              </h4>
              <div className="owcha-card-actions">
                <button className="owcha-btn-edit" onClick={() => handleEditClick(character, index)} disabled={loading}>
                  Редактировать
                </button>
                <button className="owcha-btn-delete" onClick={() => handleDelete(index)} disabled={loading}>
                  Удалить
                </button>
              </div>
              <div className="owcha-card-body">
                {character.sprite_mix_id ? (
                  <>
                    <p><strong>Набор спрайтов:</strong> {getSpriteMixName(character.character_id, character.sprite_mix_id)}</p>
                    {character.start_sprite_id && (
                      <p><strong>Начальный спрайт:</strong> {getSpriteAssetName(character.start_sprite_id)}</p>
                    )}
                  </>
                ) : (
                  <p><strong>Набор спрайтов:</strong> Нет</p>
                )}
              </div>
            </div>
          ))
        ) : (
          <div className="owcha-empty">
            <p>В этой сцене пока нет персонажей.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default OpenWorldSceneCharacters;
